// FORMS_V2
import React, { useEffect, useState, useCallback } from 'react';
import AuditModal from './AuditModal';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATUS_KLEUREN = {
  ingediend:   { bg: '#dbeafe', fg: '#1e40af', label: '📤 Ingediend' },
  goedgekeurd: { bg: '#d1fae5', fg: '#065f46', label: '✅ Goedgekeurd' }
};
const WORKFLOW_KLEUREN = {
  compleet:           { bg: '#d1fae5', fg: '#065f46', label: '✅ Compleet' },
  wachten_op_signers: { bg: '#fef3c7', fg: '#92400e', label: '⏳ Wacht op tekenen' },
  geweigerd:          { bg: '#fee2e2', fg: '#991b1b', label: '✗ Geweigerd / ingetrokken' },
  verlopen:           { bg: '#e2e8f0', fg: '#475569', label: '⌛ Verlopen' }
};
const SIGNER_KLEUREN = {
  wachten:   { bg: '#fef3c7', fg: '#92400e', icon: '⏳' },
  getekend:  { bg: '#d1fae5', fg: '#065f46', icon: '✓' },
  geweigerd: { bg: '#fee2e2', fg: '#991b1b', icon: '✗' }
};
const STATUS_FILTERS = [
  ['alle', 'Alle'], ['wacht', '⏳ Wacht'], ['compleet', '✅ Compleet'], ['geweigerd', '✗ Geweigerd'], ['verlopen', '⌛ Verlopen']
];

export default function FormsInbox({ limit = 5, showFilter = false, compact = false }) {
  const [items, setItems] = useState([]);
  const [auditModalSubId, setAuditModalSubId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periode, setPeriode] = useState('alles');
  const [statusFilter, setStatusFilter] = useState('alle');
  const [zoek, setZoek] = useState('');
  const [busyId, setBusyId] = useState(null);
  const navigate = useNavigate();
  const { terms } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/form-submissions/inbox?limit=${limit}&periode=${periode}`);
      setItems(r.data?.submissions || []);
    } catch (e) {
      console.error('Inbox laden mislukt:', e);
    } finally {
      setLoading(false);
    }
  }, [limit, periode]);

  useEffect(() => { load(); }, [load]);

  const fmtTijd = (d) => {
    if (!d) return '-';
    const date = new Date(d);
    const isToday = date.toDateString() === new Date().toDateString();
    if (isToday) return date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' });
  };
  const fmtVol = (d) => d ? new Date(d).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  const markeerGelezenEnOpen = async (id) => {
    try { await api.post(`/form-submissions/${id}/markeer-gelezen`); } catch {}
    navigate(`/forms/invullen/${id}`);
  };

  const navContext = (s, e) => {
    e.stopPropagation();
    if (s.werkorder_id) navigate(`/werkorder/${s.werkorder_id}`);
    else if (s.project_id) navigate(`/project/${s.project_id}`);
    else if (s.klant_id) navigate(`/klant/${s.klant_id}`);
  };
  const hasContext = (s) => s.werkorder_id || s.project_id || s.klant_id;

  const herinner = async (s, e) => {
    e.stopPropagation();
    const open = (s.signers || []).filter(x => x.status === 'wachten' && x.uitgenodigd_op);
    if (!open.length) return;
    if (!window.confirm(`Herinnering sturen naar ${open.map(x => x.naam).join(', ')}?`)) return;
    setBusyId(s.id);
    try {
      const r = await api.post(`/forms-workflow/${s.id}/herinner`);
      const ok = (r.data?.mail_results || []).filter(m => m.success).length;
      alert(`Herinnering verstuurd: ${ok}/${open.length}`);
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally { setBusyId(null); }
  };

  // FORMS_V2B: link van een wachtende ondertekenaar ophalen -> nu tekenen (tablet) of kopiëren (WhatsApp e.d.)
  const signerLink = async (sub, x, e) => {
    e.stopPropagation();
    try {
      const r = await api.get(`/forms-workflow/${sub.id}/signer-link/${x.id}`);
      const url = r.data.url;
      const nu = window.confirm(`${x.naam}\n\nOK = nu op dit apparaat laten tekenen (opent de tekenpagina)\nAnnuleren = alleen link kopiëren`);
      if (nu) { window.open(url, '_blank'); return; }
      try { await navigator.clipboard.writeText(url); alert('Link gekopieerd:\n' + url); }
      catch { window.prompt('Kopieer de link:', url); }
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  const intrekken = async (s, e) => {
    e.stopPropagation();
    const reden = window.prompt('Document intrekken — reden (optioneel):', 'Ingetrokken door verzender');
    if (reden === null) return;
    setBusyId(s.id);
    try {
      await api.post(`/forms-workflow/${s.id}/intrekken`, { reden });
      await load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally { setBusyId(null); }
  };

  const zichtbaar = items.filter(s => {
    if (statusFilter === 'wacht' && s.workflow_status !== 'wachten_op_signers') return false;
    if (statusFilter === 'compleet' && !(s.workflow_status === 'compleet' || (s.workflow_status === 'draft' && s.status === 'goedgekeurd'))) return false;
    if (statusFilter === 'geweigerd' && s.workflow_status !== 'geweigerd') return false;
    if (statusFilter === 'verlopen' && s.workflow_status !== 'verlopen') return false;
    if (zoek) {
      const q = zoek.toLowerCase();
      const hay = [s.template_naam, s.ingevuld_door_naam, s.klant_naam, s.werkorder_titel, s.project_naam, String(s.id),
        ...(s.signers || []).map(x => x.naam + ' ' + x.email)].join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const counts = {
    wacht: items.filter(s => s.workflow_status === 'wachten_op_signers').length,
    compleet: items.filter(s => s.workflow_status === 'compleet').length,
    geweigerd: items.filter(s => s.workflow_status === 'geweigerd').length,
    verlopen: items.filter(s => s.workflow_status === 'verlopen').length
  };

  if (loading) return <div style={{ padding: 12, color: '#64748b', fontSize: 13 }}>Forms inbox laden…</div>;

  return (
    <div>
      {showFilter && (
        <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {['vandaag', 'week', 'maand', 'alles'].map(p => (
            <button key={p} onClick={() => setPeriode(p)} style={pill(periode === p)}>
              {p === 'vandaag' ? 'Vandaag' : p === 'week' ? 'Deze week' : p === 'maand' ? 'Deze maand' : 'Alles'}
            </button>
          ))}
          <span style={{ width: 1, height: 22, background: '#e2e8f0', margin: '0 4px' }} />
          {STATUS_FILTERS.map(([v, l]) => (
            <button key={v} onClick={() => setStatusFilter(v)} style={pill(statusFilter === v)}>
              {l}{v !== 'alle' && counts[v] > 0 ? ` (${counts[v]})` : ''}
            </button>
          ))}
          <input value={zoek} onChange={e => setZoek(e.target.value)} placeholder="Zoek op naam, ondertekenaar, #…"
            style={{ marginLeft: 'auto', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, minWidth: 220 }} />
        </div>
      )}

      {zichtbaar.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontStyle: 'italic', fontSize: 13 }}>
          Geen formulieren{statusFilter !== 'alle' || zoek ? ' voor dit filter' : ' binnen'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {zichtbaar.map(s => {
            const wfKey = (s.workflow_status && s.workflow_status !== 'draft') ? s.workflow_status : null;
            const k = (wfKey && WORKFLOW_KLEUREN[wfKey]) || STATUS_KLEUREN[s.status] || { bg: '#f1f5f9', fg: '#475569', label: s.status };
            const isUnread = !s.gelezen;
            const signers = Array.isArray(s.signers) ? s.signers : [];
            const nOpen = signers.filter(x => x.status === 'wachten' && x.uitgenodigd_op).length;
            const nDone = signers.filter(x => x.status === 'getekend').length;
            const wacht = s.workflow_status === 'wachten_op_signers';
            const busy = busyId === s.id;
            return (
              <div
                key={s.id}
                onClick={() => markeerGelezenEnOpen(s.id)}
                style={{
                  background: isUnread ? '#eff6ff' : 'white',
                  border: `1px solid ${isUnread ? '#bfdbfe' : '#e2e8f0'}`,
                  borderLeft: `3px solid ${wacht ? '#f59e0b' : isUnread ? '#3b82f6' : '#e2e8f0'}`,
                  borderRadius: 8, padding: compact ? 10 : 14,
                  cursor: 'pointer', transition: 'all 0.15s', opacity: busy ? 0.6 : 1
                }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: isUnread ? 700 : 600, fontSize: 14, color: '#1e293b', marginBottom: 4 }}>
                      {isUnread && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#3b82f6', flexShrink: 0 }}></span>}
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.template_naam} #{s.id}</span>
                      {s.signing_strength === 'aes' && <span title="Geavanceerde handtekening (AES): SMS-verificatie + tijdstempel" style={{ fontSize: 12 }}>🔐</span>}
                      {s.kopie_naar_signers === false && <span title="Ondertekenaars krijgen géén kopie" style={{ fontSize: 11, color: '#94a3b8' }}>geen kopie</span>}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <span>{s.ingevuld_door_naam || '–'}</span>
                      {parseInt(s.aantal_handtekeningen, 10) > 0 && <span>✍️ {s.aantal_handtekeningen}</span>}
                      {s.klant_naam && <span>👤 {s.klant_naam}</span>}
                      {s.werkorder_titel && <span>🔧 {s.werkorder_titel}</span>}
                      {s.project_naam && <span>📁 {s.project_naam}</span>}
                    </div>

                    {/* Signer-lijst */}
                    {signers.length > 0 && !compact && (
                      <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {signers.map(x => {
                          const nietUitgenodigd = x.status === 'wachten' && !x.uitgenodigd_op;
                          const c = nietUitgenodigd ? { bg: '#f1f5f9', fg: '#64748b', icon: '⏸' } : (SIGNER_KLEUREN[x.status] || SIGNER_KLEUREN.wachten);
                          const tip = x.status === 'getekend' ? `Getekend ${fmtVol(x.getekend_op)}` : x.status === 'geweigerd' ? `Geweigerd ${fmtVol(x.geweigerd_op)}` : nietUitgenodigd ? 'Wacht op eerdere ondertekenaar (nog geen link ontvangen)' : `Uitgenodigd ${fmtVol(x.uitgenodigd_op)}${x.aantal_herinneringen ? ` · ${x.aantal_herinneringen}× herinnerd` : ''}`;
                          const klikbaar = x.status === 'wachten' && x.uitgenodigd_op && s.workflow_status === 'wachten_op_signers';
                          return (
                            <span key={x.id} title={`${x.email} — ${tip}${klikbaar ? '\nKlik: nu laten tekenen of link kopiëren' : ''}`}
                              onClick={klikbaar ? (e) => signerLink(s, x, e) : undefined}
                              style={{ background: c.bg, color: c.fg, padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, cursor: klikbaar ? 'pointer' : 'default', textDecoration: klikbaar ? 'underline dotted' : 'none' }}>
                              {c.icon} {s.tekenvolgorde === 'sequentieel' ? `${x.volgorde}. ` : ''}{x.naam}{klikbaar ? ' 🔗' : ''}
                            </span>
                          );
                        })}
                        {signers.length > 1 && (
                          <span style={{ fontSize: 11, color: '#94a3b8', alignSelf: 'center' }}>{nDone}/{signers.length} getekend</span>
                        )}
                      </div>
                    )}

                    {/* Mini timeline */}
                    <div style={{ marginTop: 6, display: 'flex', gap: 10, fontSize: 11, color: '#94a3b8', flexWrap: 'wrap' }}>
                      {(s.created_at || s.ingediend_op) && (
                        <span title="Aangemaakt"><span style={{ color: '#3b82f6', fontWeight: 700 }}>+</span> {fmtTijd(s.created_at || s.ingediend_op)}</span>
                      )}
                      {s.ingediend_op && (
                        <span title="Ingediend"><span style={{ color: '#10b981', fontWeight: 700 }}>✓</span> {fmtTijd(s.ingediend_op)}</span>
                      )}
                      {s.laatst_getekend_op && (
                        <span title="Laatst getekend"><span style={{ color: '#10b981', fontWeight: 700 }}>✍</span> {fmtTijd(s.laatst_getekend_op)}</span>
                      )}
                      {s.workflow_compleet_op && (
                        <span title="Workflow compleet"><span style={{ color: '#059669', fontWeight: 700 }}>★</span> {fmtTijd(s.workflow_compleet_op)}</span>
                      )}
                      {s.verloopt_op && s.workflow_status === 'wachten_op_signers' && (
                        <span title={`Geldig tot ${fmtVol(s.verloopt_op)} — daarna verloopt het automatisch`} style={{ color: new Date(s.verloopt_op) - Date.now() < 3 * 86400000 ? '#dc2626' : '#94a3b8' }}>⌛ tot {fmtTijd(s.verloopt_op)}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                    <span style={{ fontSize: 11, color: '#64748b' }}>{fmtTijd(s.ingediend_op || s.created_at)}</span>
                    <span style={{ background: k.bg, color: k.fg, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{k.label}</span>
                  </div>
                </div>

                {!compact && (
                  <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {s.gegenereerde_pdf_pad && (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); navigate('/forms/bekijken/' + s.id); }} style={btnStyle}>📄 PDF</button>
                        <button onClick={(e) => { e.stopPropagation(); window.open('/v/' + s.id, '_blank'); }} style={btnStyle} title="Verifieer authenticiteit + check of PDF is gewijzigd">🔍 Verifieer</button>
                      </>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); setAuditModalSubId(s.id); }} style={btnStyle} title="Volledige audit trail (IP, apparaat, tijdlijn)">📋 Audit</button>
                    {wacht && nOpen > 0 && (
                      <button disabled={busy} onClick={(e) => herinner(s, e)} style={{ ...btnStyle, background: '#fef3c7', borderColor: '#fcd34d', color: '#92400e' }} title="Magic-link opnieuw mailen naar wie nog niet getekend heeft">
                        🔔 Herinner ({nOpen})
                      </button>
                    )}
                    {wacht && (
                      <button disabled={busy} onClick={(e) => intrekken(s, e)} style={{ ...btnStyle, background: '#fee2e2', borderColor: '#fca5a5', color: '#991b1b' }} title="Document intrekken; open links werken niet meer">
                        ⛔ Intrekken
                      </button>
                    )}
                    {hasContext(s) && (
                      <button onClick={(e) => navContext(s, e)} style={btnStyle} title="Naar gekoppelde context">
                        → {s.werkorder_id ? 'Werkorder' : s.project_id ? 'Project' : (terms?.klant_singular || 'Klant')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {auditModalSubId && (
        <AuditModal submissionId={auditModalSubId} onClose={() => setAuditModalSubId(null)} />
      )}
    </div>
  );
}

const pill = (active) => ({
  padding: '6px 14px', borderRadius: 20, border: 'none',
  background: active ? '#1e3a5f' : '#e5e7eb', color: active ? 'white' : '#374151',
  cursor: 'pointer', fontSize: 13, fontWeight: 500
});
const btnStyle = {
  padding: '4px 10px', background: '#f1f5f9', border: '1px solid #cbd5e1',
  borderRadius: 4, cursor: 'pointer', fontSize: 12, color: '#475569'
};
